/**
 * Widget Preview API
 *
 * API client for fetching widget preview data from the backend
 */

export interface WidgetPreviewResponse {
  html: string;
  ui?: unknown;
  serverId: string;
  workspaceId: string;
  widget: {
    _id: string;
    slug: string;
    displayName: string;
    description: string | null;
    widgetKind: string | null;
  };
  resource: {
    uri: string;
    mimeType: string | null;
  };
}

export interface WidgetPreviewError {
  error: string;
  details?: string;
}

/**
 * Fetch widget preview data by canonical widget ID
 * @param canonicalWidgetId The ID of the canonical widget
 */
export async function fetchWidgetPreview(
  canonicalWidgetId: string,
): Promise<WidgetPreviewResponse> {
  const res = await fetch(`/api/mcp/widgets/${canonicalWidgetId}/preview`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  if (!res.ok) {
    let bodyText = "";
    try {
      bodyText = await res.text();
      const parsed = JSON.parse(bodyText);
      const errorMsg = parsed.error || res.statusText;
      const details = parsed.details ? ` (${parsed.details})` : "";
      throw new Error(
        `Widget preview failed (${res.status}): ${errorMsg}${details}`,
      );
    } catch (parseError) {
      // If parsing failed or error was thrown above, use body text or status
      if (
        parseError instanceof Error &&
        parseError.message.startsWith("Widget preview failed")
      ) {
        throw parseError;
      }
      throw new Error(
        `Widget preview failed (${res.status}): ${bodyText || res.statusText}`,
      );
    }
  }

  let body: any = null;
  try {
    body = await res.json();
  } catch (error) {
    throw new Error(`Failed to parse widget preview response`);
  }

  return body as WidgetPreviewResponse;
}
